import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { t } from 'i18next';
import { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ProviderTypes } from '../../common/Constants';
import { PortalIdentifier } from '../../common/Portal';
import { PortalContext } from '../../context/PortalContext';
import { gehaThemeOverride } from '../../frontends/themeOverrides/gehaThemeOverride';
import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useSuppressNiceMembers } from '../../hooks/useSuppressNiceMembers';
import { FacilityDDPCode, Provider } from '../../models/Provider';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import {
  displayPreferredFacilityOnCards,
  getPremiumCarePhysician,
  getSuppressFacilityFlags,
  isShowPreferredFacilityBadge,
} from '../../utils/providerDetails.utils';
import {
  getCurrentMember,
  getIsOxfordAndUSP,
  getLoggedInMember,
  useCustomPolicy,
} from '../../utils/user.utils';
import { adobeSearchResultClickEvent } from '../AdobeTagging/adobeSearchResultClickEvent';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { StyledDiv, mobileOnly } from '../ConstantsStyles';
import { getFeatureFlag } from '../Utils';
import {
  convertProviderTypeToAdobeType,
  formatProviderId,
} from '../Utils/adobeTrackUtils/adobeTrackUtils';
import { getTierProviderEligibility } from '../Utils/memberUtils/memberUtils';
import {
  BottomWrapper,
  CardContainer,
  FacilityIconStyles,
  FacilityTagStyle,
  FacilityText,
  FacilityTextStyle,
  GetStartedFlexStyle,
  HeadingExternalWrapper,
  MapViewText,
  NetworkIconStyles,
  NetworkText,
  NetworkWrapper,
  ResponsiveText,
} from './DataCard.style';
import {
  displayPremiumCareIdentifier,
  displayTier1Identifier,
  displayTier2Identifier,
} from './DataCardUtils';

type Props = {
  facilityName: string;
  headers?: ResponseHeaders;
  providerSpeciality: string;
  providerId: string;
  networkStatus: any;
  cardRef: any;
  highlightClassName: string;
  css: any;
  setHighlightId: any;
  practitioner?: Provider;
  website: string;
  index: number;
  linkLocationName: string;
  selectedFilters?: string;
  updatePin?: () => void;
  enableMapEnhancements?: boolean;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
};

const cardContainerBorderCss = (enableMapEnhancements) =>
  enableMapEnhancements
    ? {
        '&.active': {
          border: '2px solid $core.color.brand.80',
        },
      }
    : '';

const getFacilityTextColor = (enableMapViewHideOption, isGEHAV2, isGeha) =>
  enableMapViewHideOption
    ? isGEHAV2
      ? '$core.color.brand.60'
      : isGeha
      ? gehaThemeOverride.theme.colors.facilitynameTextColor
      : '$core.color.brand.100'
    : '$core.color.brand.100';

export const DataCardWithExternalLink = ({
  facilityName,
  headers = { correlationId: '' },
  practitioner = {},
  providerSpeciality,
  providerId,
  networkStatus,
  cardRef,
  highlightClassName,
  css,
  setHighlightId,
  website,
  index,
  linkLocationName,
  selectedFilters,
  updatePin,
  enableMapEnhancements,
  expandedProviderListView,
  enableMapViewHideOption,
}: Props) => {
  const isGreenDiamondIndicator = practitioner?.isGreenDiamondProvider;
  const currentMember = getCurrentMember();
  const mobileScreen = useMediaQuery(mobileOnly);

  const suppressPremiumCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();
  const {
    isTier2Provider,
    isPremiumCarePhysician,
    isPreferredLab = false,
    isFreeStandingFacility = false,
    isOxPreferredLab,
    isTieredProvider,
  } = practitioner || {};

  const isTier1ProviderEligible = displayTier1Identifier(isTieredProvider);

  const isDisplayTier1Identifier = getTierProviderEligibility(
    currentMember,
    isTier1ProviderEligible
  );

  const isDisplayTier2Identifier = displayTier2Identifier(
    isTier2Provider,
    currentMember?.isIFPTieredPlan
  );

  const memberHasRPK = useStore(StoreKeys.TIER1_PLAN_FLAG);

  const featureFlags = useLagoon('feature-flags')();

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType: coverageTypeValue, lob } = chipStore;

  const selectedMember = getLoggedInMember();
  const { isCustomPreferredPolicy } = useCustomPolicy(
    selectedMember,
    useLagoon('custom-preferred-policies'),
    lob
  );

  const showPremiumCare = displayPremiumCareIdentifier(
    !(suppressPremiumCare || suppressPremiumCareForNiceMembers),
    getPremiumCarePhysician(isPremiumCarePhysician),
    isDisplayTier1Identifier,
    isDisplayTier2Identifier,
    memberHasRPK,
    isCustomPreferredPolicy
  );

  const coverageType = coverageTypeValue;

  const suppressFacilityFlags = getSuppressFacilityFlags();

  const preferredFacilityFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.PREFERRED_FACILITY_IDENTIFICATION
  );
  const ddpCodeUser = getLoggedInMember()?.ddpCode;
  const ddpCode = practitioner?.ddpCode as FacilityDDPCode;
  const { isOXMember } = getIsOxfordAndUSP(
    currentMember,
    coverageType,
    featureFlags
  );
  const oxfordPlnFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN
  );

  const removeTruncatedDataCard: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.REMOVE_TRUNCATION_DATACARD
  );

  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const showBenefitsBadge = isShowPreferredFacilityBadge(
    suppressFacilityFlags,
    preferredFacilityFlag,
    ddpCode,
    ddpCodeUser,
    isPreferredLab,
    isOxPreferredLab,
    isOXMember,
    isFreeStandingFacility,
    oxfordPlnFlag
  );

  const isDisplayPreferredFacilityOnCards =
    !isGreenDiamondIndicator &&
    !!displayPreferredFacilityOnCards(
      showPremiumCare,
      showBenefitsBadge,
      isDisplayTier1Identifier,
      isDisplayTier2Identifier
    );

  const handleOnClick = (website) => {
    adobeSearchResultClickEvent({
      filters: selectedFilters,
      linkName: 'get-started-national-ancillary-facility',
      linkPosition: index + 1,
      linkLocation: linkLocationName,
      customAttributesBlock: {
        correlationId: headers.correlationId,
        providerId: formatProviderId(practitioner),
        providerType: convertProviderTypeToAdobeType(
          ProviderTypes.ORGANIZATION
        ),
        destinationUrl: website,
        linkType: 'external',
      },
    });
    window.open(website);
  };

  return (
    <CardContainer
      className={highlightClassName}
      css={
        !enableMapViewHideOption
          ? {
              ...css,
              ...cardContainerBorderCss(enableMapEnhancements),
              '&.abyss-card-root': {
                '@media (max-width: 464px)': {
                  height: '178px',
                },
              },
            }
          : {
              paddingLeft: expandedProviderListView ? '10px' : '0px',
              '@media (max-width: 984px)': {
                paddingLeft: '0px !important',
              },
              '@media (max-width: 744px)': {
                paddingLeft: '0px !important',
                height: 'auto !important',
                width: '100% !important',
              },
            }
      }
      cssProps={{
        expandedProviderListView,
        enableMapViewHideOption,
        isNewMapViewDataCardDesign: false,
      }}
      data-auto-testid="data-card-with-external-link"
      data-testid="data-card-with-external-link"
      id={`data-card-for-map-${providerId}`}
      onClick={() => handleOnClick(website)}
      onMouseEnter={() => {
        if (updatePin) {
          updatePin();
        }
        setHighlightId({ providerId });
      }}
      ref={cardRef}
      style={{ cursor: 'pointer' }}
    >
      <StyledDiv
        css={{
          padding: expandedProviderListView ? ' 24px 16px 0px 0px' : '',
          height: enableMapViewHideOption ? 'auto' : '100px',
          '@media (max-width: 744px)': {
            height: enableMapViewHideOption ? 'auto' : '100px',
          },
        }}
      >
        <Flex
          css={{ flex: 1, marginLeft: '12px', marginRight: '-12px' }}
          direction="column"
        >
          <Flex justify="space-between">
            <HeadingExternalWrapper
              alignLayout="left"
              cssProps={{ enableMapViewHideOption, expandedProviderListView }}
              data-auto-testid={`data-card-heading-national-ancillary-facility-${providerId}`}
              data-testid={`data-card-heading-national-ancillary-facility-${providerId}`}
            >
              <Tooltip asChild={false} content={facilityName}>
                <FacilityText
                  color={getFacilityTextColor(
                    enableMapViewHideOption,
                    isGEHAV2,
                    isGeha
                  )}
                  cssProps={{
                    expandedProviderListView,
                    enableMapViewHideOption,
                    removeTruncatedDataCard,
                  }}
                  data-auto-testid={`data-card-detail-button-national-ancillary-facility`.replace(
                    / /g,
                    '-'
                  )}
                  data-testid={`data-card-detail-button-national-ancillary-facility`.replace(
                    / /g,
                    '-'
                  )}
                  size="$lg"
                >
                  {facilityName}
                </FacilityText>

                {enableMapViewHideOption && (
                  <FacilityIconStyles
                    color={isGehaTheme ? '$core.color.brand.60' : '#196ECF'}
                    cssProps={{
                      expandedProviderListView,
                      removeTruncatedDataCard,
                    }}
                    icon="open_in_new"
                  />
                )}
              </Tooltip>
            </HeadingExternalWrapper>
            {expandedProviderListView && !mobileScreen && (
              <FacilityTagStyle
                alignItems="flex-end"
                cssProps={{ isAncillaryCard: true }}
                direction="column"
              >
                {isDisplayPreferredFacilityOnCards ? (
                  <Flex alignItems="center" direction="row">
                    <FacilityTextStyle
                      cssProps={{
                        expandedProviderListView,
                        enableMapViewHideOption,
                        enableMapEnhancements,
                        isGEHAV2,
                      }}
                    >
                      {t('PREFERRED_FACILITY_BENEFITS.TAG')}
                    </FacilityTextStyle>
                  </Flex>
                ) : null}
              </FacilityTagStyle>
            )}
          </Flex>
          <Flex justify="space-between">
            <Flex direction="column">
              {providerSpeciality && (
                <Tooltip asChild={false} content={providerSpeciality}>
                  {enableMapViewHideOption ? (
                    <MapViewText
                      color={
                        isGEHAV2
                          ? '$core.color.brand.100'
                          : '$core.color.neutral.90'
                      }
                      cssProps={{ expandedProviderListView }}
                      data-auto-testid="data-card-specialty-national-ancillary-facility"
                      data-testid="data-card-specialty-national-ancillary-facility"
                    >
                      {providerSpeciality}
                    </MapViewText>
                  ) : (
                    <ResponsiveText
                      ancillarySpeciality
                      color={isGEHAV2 ? '$core.color.brand.100' : '#212223'}
                      data-auto-testid="data-card-specialty-national-ancillary-facility"
                      data-testid="data-card-specialty-national-ancillary-facility"
                    >
                      {providerSpeciality}
                    </ResponsiveText>
                  )}
                </Tooltip>
              )}
              <Flex alignItems="center" />
            </Flex>
          </Flex>
          {(!expandedProviderListView || mobileScreen) && (
            <Flex
              alignItems="flex-start"
              css={{
                display: 'flex',
                width: '100%',
                gap: '8px',
                flex: '1 0 0',
                paddingTop: '16px',
                '@media (max-width: 744px)': {
                  paddingRight: '0px',
                },
              }}
              direction="column"
            >
              {isDisplayPreferredFacilityOnCards ? (
                <Flex alignItems="center" direction="row">
                  <FacilityTextStyle
                    cssProps={{
                      expandedProviderListView,
                      enableMapViewHideOption,
                      enableMapEnhancements,
                      isGEHAV2,
                    }}
                  >
                    {t('PREFERRED_FACILITY_BENEFITS.TAG')}
                  </FacilityTextStyle>
                </Flex>
              ) : null}
            </Flex>
          )}
          {enableMapViewHideOption && (
            <Flex
              alignItems="center"
              css={{
                flexWrap: 'nowrap',
                display: expandedProviderListView ? 'flex' : 'initial',
                '@media (max-width: 744px)': {
                  display: 'initial',
                },
              }}
              justify="space-between"
            >
              <Flex
                alignItems="start"
                css={{
                  paddingTop: isDisplayPreferredFacilityOnCards ? '6px' : '0px',
                  paddingBottom: expandedProviderListView ? '10px' : '0px',
                  '@media (max-width: 744px)': {
                    paddingBottom: '0px',
                  },
                }}
                data-testid="data-card-network-national-ancillary-facility"
                direction={expandedProviderListView ? 'column' : 'row'}
              >
                <NetworkWrapper>
                  <NetworkIconStyles
                    className="check_circle"
                    color={networkStatus ? '$core.color.green.100' : ''}
                    cssProps={{
                      enableMapViewHideOption,
                      expandedProviderListView,
                    }}
                    data-auto-testid={`data-card-check-circle-icon-national-ancillary-facility-${providerId}`}
                    data-testid={`data-card-check-circle-icon-national-ancillary-facility-${providerId}`}
                    icon="check_circle"
                    size={20}
                    variant="outlined"
                  />
                  <NetworkText
                    color="#333"
                    cssProps={{ expandedProviderListView }}
                    data-auto-testid={`data-card-network-national-ancillary-facility-${providerId}`}
                    data-testid={`data-card-network-national-ancillary-facility-${providerId}`}
                  >
                    {networkStatus ? t('In-network') : t('Not-In-Network')}
                  </NetworkText>
                </NetworkWrapper>
              </Flex>
              <GetStartedFlexStyle
                alignItems="flex-end"
                cssProps={{ expandedProviderListView }}
                justify="flex-end"
              >
                <Flex
                  alignItems="center"
                  css={{
                    marginTop: expandedProviderListView ? '0px' : '16px',
                    marginBottom: '16px',
                    '@media (max-width: 984px)': {
                      marginTop: '16px',
                      marginBottom: '16px',
                    },
                  }}
                  justify="flex-end"
                >
                  <Button
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      justifyContent: 'center',
                      padding: '1px 16px',
                      border: '1px solid $core.color.brand.100',
                      borderRadius: '24px',
                      '.abyss-button-content-container': {
                        gap: '8px',
                      },
                      height: expandedProviderListView ? '40px' : '32px',
                      '@media (max-width: 984px)': {
                        height: '32px',
                      },
                    }}
                    data-auto-testid="get-started-national-ancillary-facility"
                    data-testid="get-started-national-ancillary-facility"
                    href={website}
                    onClick={(e) => e.stopPropagation()}
                    variant="outline"
                  >
                    <ResponsiveText
                      color={'$core.color.brand.100'}
                      fontWeight="bold"
                    >
                      {t('DONT_KNOW_WHERE_TO_START_CARD.GET_STARTED')}
                    </ResponsiveText>
                  </Button>
                </Flex>
              </GetStartedFlexStyle>
            </Flex>
          )}
        </Flex>
      </StyledDiv>
      {!enableMapViewHideOption && (
        <BottomWrapper
          css={{
            padding: '8px 12px',
            borderTop: '1px solid #CBCCCD',
          }}
          justify="left"
        >
          <div
            style={{
              marginTop: '8px',
              marginBottom: '8px',
            }}
          >
            <Link
              data-auto-testid="get-started-national-ancillary-facility"
              data-testid="get-started-national-ancillary-facility"
              href={website}
              onClick={(e) => e.stopPropagation()}
            >
              <Text
                color="$core.color.brand.60"
                css={{
                  'abyss-text-root': {
                    fontSize: '16px',
                    fontWeight: 700,
                  },
                }}
              >
                {t('DONT_KNOW_WHERE_TO_START_CARD.GET_STARTED')}
              </Text>
            </Link>
          </div>
        </BottomWrapper>
      )}
    </CardContainer>
  );
};
